// Performance monitoring utilities
import React from 'react';

export interface PerformanceMetrics {
  fcp: number; // First Contentful Paint
  lcp: number; // Largest Contentful Paint
  fid: number; // First Input Delay
  cls: number; // Cumulative Layout Shift
  ttfb: number; // Time to First Byte
}

// Web Vitals tracking
export const trackWebVitals = () => {
  if (typeof window === 'undefined') return;

  // Track FCP (First Contentful Paint)
  const trackFCP = () => {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const fcp = entries.find(entry => entry.name === 'first-contentful-paint');
      if (fcp) {
        console.log('FCP:', fcp.startTime);
        // Send to analytics service
        trackMetric('fcp', fcp.startTime);
      }
    });
    observer.observe({ entryTypes: ['paint'] });
  };

  // Track LCP (Largest Contentful Paint)
  const trackLCP = () => {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      console.log('LCP:', lastEntry.startTime);
      trackMetric('lcp', lastEntry.startTime);
    });
    observer.observe({ entryTypes: ['largest-contentful-paint'] });
  };

  // Track FID (First Input Delay)
  const trackFID = () => {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry: any) => {
        const fid = entry.processingStart - entry.startTime;
        console.log('FID:', fid);
        trackMetric('fid', fid);
      });
    });
    observer.observe({ entryTypes: ['first-input'] });
  };

  // Track CLS (Cumulative Layout Shift)
  const trackCLS = () => {
    let clsValue = 0;
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry: any) => {
        if (!entry.hadRecentInput) {
          clsValue += entry.value;
        }
      });
      console.log('CLS:', clsValue);
      trackMetric('cls', clsValue);
    });
    observer.observe({ entryTypes: ['layout-shift'] });
  };

  // Track TTFB (Time to First Byte)
  const trackTTFB = () => {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    if (navigation) {
      const ttfb = navigation.responseStart - navigation.requestStart;
      console.log('TTFB:', ttfb);
      trackMetric('ttfb', ttfb);
    }
  };

  // Initialize tracking
  trackFCP();
  trackLCP();
  trackFID();
  trackCLS();
  trackTTFB();
};

// Send metrics to analytics (placeholder)
const trackMetric = (name: string, value: number) => {
  // In production, send to your analytics service
  if (import.meta.env.DEV) {
    console.log(`Performance Metric - ${name}:`, value);
  }
  
  // Example: Send to Google Analytics, DataDog, etc.
  // gtag('event', 'web_vitals', { metric_name: name, metric_value: value });
};

// Memory usage monitoring
export const trackMemoryUsage = () => {
  if (typeof window === 'undefined' || !('memory' in performance)) return;

  const memory = (performance as any).memory;
  const memoryInfo = {
    usedJSHeapSize: memory.usedJSHeapSize,
    totalJSHeapSize: memory.totalJSHeapSize,
    jsHeapSizeLimit: memory.jsHeapSizeLimit
  };

  console.log('Memory Usage:', memoryInfo);
  return memoryInfo;
};

// Bundle size monitoring
export const trackBundleSize = () => {
  if (typeof window === 'undefined') return;

  const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
  const jsResources = resources.filter(resource => 
    resource.name.includes('.js') && !resource.name.includes('node_modules')
  );

  const totalSize = jsResources.reduce((total, resource) => {
    return total + (resource.transferSize || 0);
  }, 0);

  console.log('Total JS Bundle Size:', (totalSize / 1024).toFixed(2), 'KB');
  return totalSize;
};

// Component render time tracking
export const withPerformanceTracking = <P extends object>(
  Component: React.ComponentType<P>,
  componentName: string
) => {
  return React.memo((props: P) => {
    React.useEffect(() => {
      const startTime = performance.now();
      
      return () => {
        const endTime = performance.now();
        const renderTime = endTime - startTime;
        console.log(`${componentName} render time:`, renderTime.toFixed(2), 'ms');
      };
    });

    return React.createElement(Component, props);
  });
};

// Initialize performance monitoring
export const initPerformanceMonitoring = () => {
  if (typeof window === 'undefined') return;

  // Track web vitals
  trackWebVitals();

  // Track memory usage every 30 seconds
  setInterval(trackMemoryUsage, 30000);

  // Track bundle size on load
  window.addEventListener('load', trackBundleSize);

  console.log('Performance monitoring initialized');
};
